import { useTheme } from "@emotion/react";
import {
	Autocomplete,
	Button,
	TextField,
	Grid,
	useMediaQuery,
	ButtonGroup,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import debounce from "lodash.debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { renderCodeOption } from "../../utils/functions";
import PrintsSearch3 from "../../components/PrintSearch3";
import PATClearButton from "../../components/Buttons/PATClearButton";

const useStyles = makeStyles((theme) => ({
	sideMenuTitle: {
		fontSize: 20,
		fontWeight: 700,
		color: theme.palette.text.primary,
		marginBottom: "5px",
	},
	colorTitle: {
		color:
			theme.palette.mode === "dark"
				? "#fff !important"
				: "#000 !important",
	},
}));

const headers = [
	{ key: "typeDisplay", label: "Type" },
	{ key: "propertyNumber", label: "Property#" },
	{ key: "serialNumber", label: "Serial#" },
	{ key: "brandModel", label: "Brand / Model" },
	{ key: "conditionDisplay", label: "Condition" },
	{ key: "statusDisplay", label: "Status" },
	{ key: "location", label: "Location" },
	{ key: "employee", label: "Employee" },
	{ key: "vehicle", label: "Vehicle" },
	{ key: "serviceDue", label: "Service Due" },
];

const columnsForPrint = headers.map((h) => ({
	field: h.key,
	headerName: h.label,
	editable: false,
	flex: 1,
	...h,
}));

const EquipmentsTableToolbar = ({
	openAdd,
	search,
	setSearch,
	onFilterChange,
	filters,
	resetFilters,
	onAuditClick,
	onAssignClick,
	typeOptions = [],
	statusOptions = [],
	conditionOptions = [],
	categoryOptions = [],
	divisionOptions = [],
	employeeOptions = [],
	vehicleOptions = [],
	fundingOptions = [],
	brandOptions = [],
	styleOptions = [],
	caliberOptions = [],
	sightsOptions = [],
	locationOptions = [],
	purchasedFromOptions = [],
	equipment = [],
	getEmployeeDisplay,
	getVehicleDisplay,
	getServiceStatus,
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const isSmall = useMediaQuery(theme.breakpoints.down("md"));
	const btnStyles = {
		color: theme.palette.mode === "dark" ? "#fff" : "primary",
		borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
		"&:hover": {
			borderColor: theme.palette.mode === "dark" ? "#fff" : "primary",
			backgroundColor: theme.palette.action.hover,
		},
	};

	const [showAdvSearch, setShowAdvSearch] = useState(false);
	const [localFilters, setLocalFilters] = useState(filters);
	const [printFlag, setPrintFlag] = useState(false);
	const [localSearch, setLocalSearch] = useState(search || "");

	// Prepare print data
	const printEquipment = useMemo(() => {
		return equipment.map((eq) => {
			const { worstSvc, svcStatuses } = getServiceStatus(eq);

			// Build service due display string
			let serviceDueDisplay = "—";
			if (worstSvc) {
				const isOverdue = worstSvc.status === "Overdue";
				serviceDueDisplay = isOverdue
					? `${Math.abs(worstSvc.dueIn)}d overdue (${worstSvc.sr?.label || worstSvc.sr?.Code || ""})`
					: `${worstSvc.dueIn}d left (${worstSvc.sr?.label || worstSvc.sr?.Code || ""})`;
			} else if (svcStatuses.length > 0) {
				serviceDueDisplay = "OK";
			}

			return {
				...eq,
				typeDisplay: eq?.type || eq?.typeInfo?.Code || "—",
				conditionDisplay:
					eq?.condition || eq?.conditionInfo?.Code || "—",
				statusDisplay: eq?.status || eq?.statusInfo?.Code || "—",
				brandModel:
					`${eq?.brand || ""} ${eq?.model || ""}`.trim() || "—",
				employee: getEmployeeDisplay(eq?.perPersonnelId),
				vehicle: getVehicleDisplay(eq?.vehicleId),
				serviceDue: serviceDueDisplay, // Add this
			};
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [equipment, getEmployeeDisplay, getVehicleDisplay, getServiceStatus]);

	useEffect(() => {
		setLocalFilters(filters);
	}, [filters]);

	useEffect(() => {
		setLocalSearch(search || "");
	}, [search]);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearchChange = useCallback(
		debounce((searchString) => {
			setSearch(searchString);
		}, 500),
		[setSearch],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedFilterChange = useCallback(
		debounce((newFilters) => {
			onFilterChange(newFilters);
		}, 500),
		[onFilterChange],
	);

	const handleSearchChange = (e) => {
		const value = e.target.value;
		setLocalSearch(value);
		debouncedSearchChange(value);
	};

	const handleFilterChange = useCallback(
		(field, value) => {
			const updatedFilters = { ...localFilters, [field]: value };
			setLocalFilters(updatedFilters);
			debouncedFilterChange(updatedFilters);
		},
		[debouncedFilterChange, localFilters],
	);

	const handlePrintClick = () => {
		setPrintFlag(true);
		setTimeout(() => setPrintFlag(false), 1000);
	};

	const renderFilterFields = (fields) =>
		fields.map(
			({ type, label, field, options, xs = 2, multiselect = true }) => (
				<Grid item xs={isSmall ? 12 : xs} key={field}>
					{type === "text" ? (
						<TextField
							label={label}
							value={localFilters[field] || ""}
							onChange={(e) =>
								handleFilterChange(field, e.target.value)
							}
							fullWidth
							variant="outlined"
							size="small"
						/>
					) : (
						<Autocomplete
							multiple={multiselect}
							options={options || []}
							value={
								multiselect
									? localFilters[field] || []
									: localFilters[field] || null
							}
							onChange={(_, value) =>
								handleFilterChange(field, value)
							}
							getOptionLabel={(option) => option?.label || ""}
							renderOption={renderCodeOption}
							isOptionEqualToValue={(o, v) =>
								o?.value === v?.value
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label={label}
									variant="outlined"
									size="small"
								/>
							)}
							fullWidth
							sx={
								multiselect
									? {
											"& .MuiAutocomplete-inputRoot": {
												flexWrap: "nowrap",
												maxHeight: "40px",
											},
											"& .MuiAutocomplete-tag": {
												maxWidth: "100%",
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
											},
											"& .MuiAutocomplete-endAdornment": {
												alignSelf: "flex-start",
											},
										}
									: {}
							}
						/>
					)}
				</Grid>
			),
		);

	const filterFields = [
		{
			type: "autocomplete",
			label: "Type",
			field: "type",
			options: typeOptions,
			xs: 1.5,
			multiselect: true,
		},
		{
			type: "autocomplete",
			label: "Employee",
			field: "perPersonnelId",
			options: employeeOptions,
			xs: 1.5,
			multiselect: true,
		},
	];

	const advanceFilterFields = [
		{
			type: "autocomplete",
			label: "Status",
			field: "status",
			options: statusOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Condition",
			field: "condition",
			options: conditionOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Division",
			field: "division",
			options: divisionOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Brand",
			field: "brand",
			options: brandOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Style",
			field: "style",
			options: styleOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Category",
			field: "categoryCode",
			options: categoryOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Vehicle",
			field: "vehicleId",
			options: vehicleOptions.map((v) => ({
				label: v.label,
				value: v.value,
			})),
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Funding",
			field: "fundingSourceId",
			options: fundingOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Purchased From",
			field: "purchasedFrom",
			options: purchasedFromOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Caliber",
			field: "caliber",
			options: caliberOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Sights",
			field: "sights",
			options: sightsOptions,
			multiselect: true,
			xs: 1.2,
		},
		{
			type: "autocomplete",
			label: "Location",
			field: "location",
			options: locationOptions,
			multiselect: true,
			xs: 1.2,
		},
	];

	const buttons = [
		<Button
			key="all"
			variant={
				filters.assignmentType === "all" ? "contained" : "outlined"
			}
			sx={filters.assignmentType === "all" ? {} : btnStyles}
			onClick={() => handleFilterChange("assignmentType", "all")}
		>
			All
		</Button>,
		<Button
			key="out"
			variant={
				filters.assignmentType === "out" ? "contained" : "outlined"
			}
			sx={filters.assignmentType === "out" ? {} : btnStyles}
			onClick={() => handleFilterChange("assignmentType", "out")}
		>
			↑
		</Button>,
		<Button
			key="in"
			variant={filters.assignmentType === "in" ? "contained" : "outlined"}
			sx={filters.assignmentType === "in" ? {} : btnStyles}
			onClick={() => handleFilterChange("assignmentType", "in")}
		>
			↓
		</Button>,
	];

	const renderClearButton = () => (
		<Grid item>
			<PATClearButton title="Clear Filters" onClick={resetFilters} />
		</Grid>
	);

	return (
		<>
			<div>
				<div className={classes.sideMenuTitle}>Equipment</div>
			</div>
			<div className="section-actions mb-2">
				<TextField
					label="Search property#, serial#, brand..."
					type="search"
					className={classes.filter}
					variant="outlined"
					size="small"
					style={{
						width: "100%",
						maxWidth: 250,
					}}
					value={localSearch}
					onChange={handleSearchChange}
				/>
				{renderFilterFields(filterFields)}
				<ButtonGroup size="large" aria-label="Large button group">
					{buttons}
				</ButtonGroup>
				<Button
					variant="outlined"
					color="primary"
					sx={btnStyles}
					onClick={() => setShowAdvSearch((s) => !s)}
				>
					🔍 Advanced
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={onAuditClick}
					title="Quick audit equipment"
					sx={btnStyles}
				>
					📋 Audit
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={onAssignClick}
					title="Bulk assign equipment"
					sx={btnStyles}
				>
					🗂️ Assign
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handlePrintClick}
					title="Print current list"
					sx={btnStyles}
				>
					🖨️ Print
				</Button>
				<Button variant="contained" color="primary" onClick={openAdd}>
					+ Add Equipment
				</Button>
			</div>
			{showAdvSearch && (
				<div
					style={{
						margin: "8px",
						padding: "14px 16px",
						border: "1px solid var(--border)",
						borderRadius: "8px",
						display: "flex",
						flexWrap: "wrap",
						gap: 8,
						alignItems: "center",
					}}
				>
					<span
						style={{
							fontSize: 11,
							fontWeight: 700,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "var(--text2)",
							marginRight: 4,
						}}
					>
						Filter by:
					</span>
					{renderFilterFields(advanceFilterFields)}
					{renderClearButton()}
				</div>
			)}

			<PrintsSearch3
				title="Equipment"
				data={printEquipment}
				cols={columnsForPrint}
				print={printFlag}
			/>
		</>
	);
};

export default EquipmentsTableToolbar;
